import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type TrackShareSource = "sponsor" | "artist" | "none";

export interface TrackShareMeta {
  source: TrackShareSource;
  attributionId: string | null;
  attributionName: string | null;
  hashtags: string[];
}

export async function fetchTrackShareMeta(
  trackId: string | undefined | null,
): Promise<TrackShareMeta | null> {
  if (!trackId) return null;
  const { data, error } = await (supabase as any).rpc("get_track_share_meta", {
    p_track_id: trackId,
  });
  if (error) {
    console.warn("[useTrackShareMeta] RPC error:", error);
    return null;
  }
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  return {
    source: (row.source as TrackShareSource) || "none",
    attributionId: row.attribution_id ?? null,
    attributionName: row.attribution_name ?? null,
    hashtags: (row.hashtags as string[]) || [],
  };
}

/**
 * Profile-level counterpart to fetchTrackShareMeta — resolves an artist's or
 * sponsor's own saved hashtags when sharing their profile page directly
 * (not tied to any one track).
 */
export async function fetchProfileShareMeta(
  profileId: string | undefined | null,
): Promise<TrackShareMeta | null> {
  if (!profileId) return null;
  const { data, error } = await (supabase as any).rpc("get_profile_share_meta", {
    p_profile_id: profileId,
  });
  if (error) {
    console.warn("[useTrackShareMeta] get_profile_share_meta RPC error:", error);
    return null;
  }
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  return {
    source: (row.source as TrackShareSource) || "none",
    attributionId: row.attribution_id ?? null,
    attributionName: row.attribution_name ?? null,
    hashtags: (row.hashtags as string[]) || [],
  };
}

/**
 * Build the trailing block appended to share text.
 *  - sponsor: "Sponsored by <name>\n#tag1 #tag2"
 *  - artist:  "#tag1 #tag2"   (no "by …" line)
 *  - none / no tags & not sponsored: ""
 */
export function buildShareSuffix(meta: TrackShareMeta | null): string {
  if (!meta) return "";
  const tagLine = meta.hashtags.length ? meta.hashtags.join(" ") : "";

  if (meta.source === "sponsor") {
    const head = `Sponsored by ${meta.attributionName || "Sponsor"}`;
    return tagLine ? `${head}\n${tagLine}` : head;
  }

  if (meta.source === "artist" && tagLine) {
    return tagLine;
  }

  return "";
}

export function useTrackShareMeta(trackId: string | undefined | null) {
  return useQuery({
    queryKey: ["trackShareMeta", trackId],
    queryFn: () => fetchTrackShareMeta(trackId),
    enabled: !!trackId,
    staleTime: 5 * 60 * 1000,
  });
}
